import { createElement } from 'react';
import {
  Activity,
  AlertTriangle,
  Archive,
  ArrowRight,
  Ban,
  CheckCheck,
  CheckCircle,
  CheckCircle2,
  Copy,
  Download,
  FlaskConical,
  Gavel,
  ImagePlus,
  KeyRound,
  LifeBuoy,
  ListChecks,
  ListOrdered,
  LogIn,
  LogOut,
  Package,
  PackageMinus,
  PackageOpen,
  PackagePlus,
  Pause,
  Pencil,
  Play,
  Plus,
  Power,
  PowerOff,
  QrCode,
  RefreshCcw,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Save,
  ScanLine,
  Send,
  ShieldCheck,
  Sparkles,
  SquareDashedMousePointer,
  Star,
  StarOff,
  Tag,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Truck,
  Undo2,
  Unlock,
  Upload,
  UserCog,
  UserMinus,
  UserPlus,
  Users,
  XCircle,
  type LucideIcon
} from 'lucide-react';

const ACTION_COLORS: Record<string, string> = {
  created:
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  updated: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  deleted: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  login: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  logout:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  'login.failed':
    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  'login.success':
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
};

const DEFAULT_COLOR =
  'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';

export function getActionColor(action: string): string {
  if (ACTION_COLORS[action]) return ACTION_COLORS[action];
  for (const [key, value] of Object.entries(ACTION_COLORS)) {
    if (action.includes(key)) return value;
  }
  return DEFAULT_COLOR;
}

export interface ActionStyle {
  bg: string;
  dot: string;
}

const ACTION_STYLES: Record<string, ActionStyle> = {
  created: {
    bg: 'bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-400',
    dot: 'bg-green-500'
  },
  updated: {
    bg: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
    dot: 'bg-blue-500'
  },
  deleted: {
    bg: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400',
    dot: 'bg-red-500'
  },
  login: {
    bg: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
    dot: 'bg-amber-500'
  },
  logout: {
    bg: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
    dot: 'bg-amber-500'
  }
};

const DEFAULT_STYLE: ActionStyle = {
  bg: 'bg-muted text-muted-foreground',
  dot: 'bg-muted-foreground'
};

export function getActionStyle(action: string): ActionStyle {
  if (ACTION_STYLES[action]) return ACTION_STYLES[action];
  for (const [key, value] of Object.entries(ACTION_STYLES)) {
    if (action.includes(key)) return value;
  }
  return DEFAULT_STYLE;
}

// Ordered most-specific → least-specific. Looked up exact-first, then via `includes`.
const ACTION_ICONS: Array<[string, LucideIcon]> = [
  // Auth
  ['auth.login', LogIn],
  ['client_auth.login', LogIn],
  ['auth.logout', LogOut],
  ['client_auth.logout', LogOut],
  ['login_verified', ShieldCheck],
  ['forgot_password', KeyRound],
  ['password_reset', KeyRound],
  ['password_changed', KeyRound],

  // Reception
  ['qr_verified', QrCode],
  ['count_verified', ListChecks],
  ['discrepancy_reported', AlertTriangle],
  ['item_verified', CheckCircle2],

  // Sorting
  ['session_started', Play],
  ['session_deleted', Trash2],
  ['container_scanned', ScanLine],
  ['page_scanned', ScanLine],
  ['container_completed', CheckCircle],
  ['container_refused', Ban],

  // Registration
  ['stage_advanced', ArrowRight],
  ['stage_rejected', XCircle],
  ['returned_to_registration', Undo2],
  ['returned_to_sorting', Undo2],
  ['images_uploaded', ImagePlus],
  ['sticking_extra_labels_requested', Tag],
  ['sticking_actual_count_adjusted', Pencil],
  ['page_count_set', Pencil],

  // Box
  ['box.opened', PackageOpen],
  ['box.closed', Package],
  ['box.rolled_back', RotateCcw],
  ['box.recycled', RotateCw],
  ['box.auto_reset', RotateCcw],
  ['box.status_updated', RefreshCcw],
  ['box.registration_added', PackagePlus],
  ['box.registration_removed', PackageMinus],

  // Shipment
  ['shipment.confirmed', CheckCircle2],
  ['shipment.dispatched', Truck],
  ['shipment.draft_saved', Save],
  ['shipment.boxes_assigned', Package],
  ['shipment.status_updated', RefreshCcw],

  // Page
  ['page.rotated', RotateCw],
  ['page.enhanced', Sparkles],
  ['pages.bulk_scanned', ScanLine],
  ['pages.reordered', ListOrdered],

  // Dynamic form
  ['dynamic_form.activated', ToggleRight],
  ['dynamic_form.deactivated', ToggleLeft],
  ['dynamic_form.set_default', Star],
  ['dynamic_form.unset_default', StarOff],
  ['dynamic_form.duplicated', Copy],
  ['dynamic_form.fields_synced', RefreshCw],

  // OCR
  ['ocr.started', Play],
  ['ocr.paused', Pause],
  ['ocr.resumed', Play],
  ['ocr.recovered', LifeBuoy],
  ['ocr.field_updated', Pencil],
  ['ocr.chunk_bbox_updated', SquareDashedMousePointer],
  ['ocr.all_accepted', CheckCheck],
  ['ocr.retried', RefreshCw],
  ['ocr.batches_retried', RefreshCw],
  ['ocr.page_rerun', RefreshCw],
  ['ocr.pages_rerun', RefreshCw],
  ['ocr.submitted_to_qa', Send],
  ['ocr.template_suggestions_generated', Sparkles],
  ['ocr.page_suggestions_generated', Sparkles],
  ['ocr.page_suggestion_generated', Sparkles],
  ['ocr.test_container_created', FlaskConical],
  ['ocr.test_container_pages_added', ImagePlus],
  ['ocr.test_container_deleted', Trash2],
  ['ocr_qa.', Gavel],
  ['ocr_template.custom_fields_synced', RefreshCw],
  // OMA-637 — a deleted template field is archived, then restorable.
  ['ocr_template.field_archived', Archive],
  ['ocr_template.field_restored', RotateCcw],
  ['ocr_template.field_rules_synced', RefreshCw],
  ['ocr_template.bulk_imported', Upload],
  ['ocr_template.bulk_exported', Download],

  // Repair settings
  ['repair_settings.destination_updated', Pencil],

  // Oman repair
  ['oman_repair.relabeled', Tag],
  ['oman_repair.repair_confirmed', CheckCircle2],

  // Translation
  ['translation.bulk_reverted', Undo2],
  ['translation.synced', RefreshCw],

  // User
  ['user.activated', Power],
  ['user.deactivated', PowerOff],
  ['user.unlocked', Unlock],
  ['user.role_assigned', UserPlus],
  ['user.role_removed', UserMinus],
  ['user.bulk_imported', Users],
  ['user.profile_updated', UserCog],
  ['user.driver_availability_updated', UserCog],
  ['user.operator_availability_updated', UserCog],
  ['driver.availability_updated', UserCog],
  ['operator.availability_updated', UserCog],
  ['profile_updated', UserCog],

  // Generic verb fallback (matched via `includes`)
  ['.created', Plus],
  ['.updated', Pencil],
  ['.deleted', Trash2],
  ['.activated', Power],
  ['.deactivated', PowerOff],
  ['.completed', CheckCircle],
  ['.started', Play],
  ['.duplicated', Copy]
];

export function getActionIcon(action: string): LucideIcon {
  for (const [key, icon] of ACTION_ICONS) {
    if (action === key) return icon;
  }
  for (const [key, icon] of ACTION_ICONS) {
    if (action.includes(key)) return icon;
  }
  return Activity;
}

interface ActionIconProps {
  action: string;
  className?: string;
}

export function ActionIcon({ action, className }: ActionIconProps) {
  return createElement(getActionIcon(action), {
    className,
    'aria-hidden': true
  });
}
